import { requiredRule, useFormControl } from "@mongez/react-form";
import { useClickOutside } from "core/shared";
import { Clock3Icon, Minus, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { cn } from "~/shared/lib";
import type { BaseInputProps } from "./BaseInput";
import { ErrorInput } from "./ErrorInput";
import { LabelInput } from "./LabelInput";

type TimeInputProps = BaseInputProps;
type TimeInputRangeProps = {
  minTime?: string;
  maxTime?: string;
};

type TimeParts = {
  hour: number;
  minute: number;
  period: "AM" | "PM";
};

const defaultTimeParts: TimeParts = {
  hour: 12,
  minute: 0,
  period: "AM",
};

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function parseTimeValue(value?: string): TimeParts {
  if (!value || typeof value !== "string" || !value.includes(":")) {
    return defaultTimeParts;
  }

  const [hoursString, minutesString] = value.split(":");
  const hours = Number(hoursString);
  const minutes = Number(minutesString);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return defaultTimeParts;
  }

  return {
    hour: hours % 12 || 12,
    minute: minutes,
    period: hours >= 12 ? "PM" : "AM",
  };
}

function parseTimeRangeValue(value?: string) {
  if (!value) return null;

  const normalizedValue = value.trim().toUpperCase();
  const meridiemMatch = normalizedValue.match(
    /^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM)$/,
  );

  if (meridiemMatch) {
    const hours = Number(meridiemMatch[1]);
    const minutes = Number(meridiemMatch[2] || "0");
    const period = meridiemMatch[3] as TimeParts["period"];

    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 1 ||
      hours > 12 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return { hour: hours, minute: minutes, period };
  }

  if (!normalizedValue.includes(":")) return null;

  return parseTimeValue(normalizedValue);
}

function timePartsToMinutes({ hour, minute, period }: TimeParts) {
  const normalizedHour = hour % 12 || 12;
  const militaryHour =
    period === "PM"
      ? normalizedHour === 12
        ? 12
        : normalizedHour + 12
      : normalizedHour === 12
        ? 0
        : normalizedHour;

  return militaryHour * 60 + clamp(minute, 0, 59);
}

function normalizeTimeWithinRange(
  time: TimeParts,
  minTime?: string,
  maxTime?: string,
) {
  const parsedMinTime = parseTimeRangeValue(minTime);
  const parsedMaxTime = parseTimeRangeValue(maxTime);
  const currentMinutes = timePartsToMinutes(time);

  if (parsedMinTime && currentMinutes < timePartsToMinutes(parsedMinTime)) {
    return parsedMinTime;
  }

  if (parsedMaxTime && currentMinutes > timePartsToMinutes(parsedMaxTime)) {
    return parsedMaxTime;
  }

  return time;
}

function formatTimeValue({ hour, minute, period }: TimeParts) {
  const normalizedHour = clamp(hour, 1, 12);
  const normalizedMinute = clamp(minute, 0, 60);
  const adjustedMinute = normalizedMinute === 60 ? 0 : normalizedMinute;
  const adjustedHour =
    normalizedMinute === 60
      ? normalizedHour === 12
        ? 1
        : normalizedHour + 1
      : normalizedHour;
  const adjustedPeriod =
    normalizedMinute === 60 && normalizedHour === 11
      ? period === "AM"
        ? "PM"
        : "AM"
      : period;
  const militaryHour =
    adjustedPeriod === "PM"
      ? adjustedHour === 12
        ? 12
        : adjustedHour + 12
      : adjustedHour === 12
        ? 0
        : adjustedHour;

  return `${militaryHour.toString().padStart(2, "0")}:${adjustedMinute
    .toString()
    .padStart(2, "0")}`;
}

function formatDisplayTime(value?: string) {
  if (!value) return "--:--";

  const { hour, minute, period } = parseTimeValue(value);

  return `${hour}:${minute.toString().padStart(2, "0")} ${period}`;
}

export function TimeInput({
  classes,
  label,
  minTime,
  maxTime,
  ...props
}: TimeInputProps & TimeInputRangeProps) {
  const { id, error, name, value, changeValue, otherProps } = useFormControl({
    ...props,
    type: "time",
    rules: [requiredRule, ...(props.rules || [])],
  });
  const [isOpen, setIsOpen] = useState(false);
  const [time, setTime] = useState<TimeParts>(() =>
    normalizeTimeWithinRange(
      parseTimeValue(typeof value === "string" ? value : undefined),
      minTime,
      maxTime,
    ),
  );

  const ref = useClickOutside(() => setIsOpen(false));

  useEffect(() => {
    setTime(
      normalizeTimeWithinRange(
        parseTimeValue(typeof value === "string" ? value : undefined),
        minTime,
        maxTime,
      ),
    );
  }, [value, minTime, maxTime]);

  const displayValue = useMemo(
    () => formatDisplayTime(typeof value === "string" ? value : undefined),
    [value],
  );
  const parsedMinTime = useMemo(() => parseTimeRangeValue(minTime), [minTime]);
  const parsedMaxTime = useMemo(() => parseTimeRangeValue(maxTime), [maxTime]);
  const currentTimeInMinutes = useMemo(() => timePartsToMinutes(time), [time]);
  const minTimeInMinutes = useMemo(
    () => (parsedMinTime ? timePartsToMinutes(parsedMinTime) : null),
    [parsedMinTime],
  );
  const maxTimeInMinutes = useMemo(
    () => (parsedMaxTime ? timePartsToMinutes(parsedMaxTime) : null),
    [parsedMaxTime],
  );
  const decrementDisabled =
    props.disabled ||
    props.readOnly ||
    (minTimeInMinutes !== null && currentTimeInMinutes <= minTimeInMinutes);
  const incrementDisabled =
    props.disabled ||
    props.readOnly ||
    (maxTimeInMinutes !== null && currentTimeInMinutes >= maxTimeInMinutes);

  const updateTime = (nextTime: TimeParts) => {
    const normalizedTime = normalizeTimeWithinRange(nextTime, minTime, maxTime);

    setTime(normalizedTime);
    const formattedValue = formatTimeValue(normalizedTime);
    changeValue(formattedValue);
    props.onChange?.(formattedValue);
  };

  const updateHour = (nextHour: number) => {
    updateTime({
      ...time,
      hour: clamp(nextHour, 1, 12),
    });
  };

  const updateMinute = (nextMinute: number) => {
    updateTime({
      ...time,
      minute: clamp(nextMinute, 0, 60),
    });
  };

  const incrementHour = () => {
    if (time.hour === 11) {
      updateTime({
        ...time,
        hour: 12,
        period: time.period === "AM" ? "PM" : "AM",
      });

      return;
    }

    updateHour(time.hour === 12 ? 1 : time.hour + 1);
  };

  const decrementHour = () => {
    if (time.hour === 12) {
      updateTime({
        ...time,
        hour: 11,
        period: time.period === "AM" ? "PM" : "AM",
      });

      return;
    }

    updateHour(time.hour === 1 ? 12 : time.hour - 1);
  };

  const incrementMinute = () => {
    if (time.minute >= 59) {
      updateTime({
        ...time,
        minute: 0,
        hour: time.hour === 12 ? 1 : time.hour + 1,
        period:
          time.hour === 11 ? (time.period === "AM" ? "PM" : "AM") : time.period,
      });

      return;
    }

    updateMinute(time.minute + 1);
  };

  const decrementMinute = () => {
    if (time.minute === 0) {
      updateTime({
        ...time,
        minute: 59,
        hour: time.hour === 12 ? 11 : time.hour === 1 ? 12 : time.hour - 1,
        period:
          time.hour === 12 ? (time.period === "AM" ? "PM" : "AM") : time.period,
      });

      return;
    }

    updateMinute(time.minute - 1);
  };

  const togglePeriod = (period: TimeParts["period"]) => {
    updateTime({
      ...time,
      period,
    });
  };

  return (
    <div
      ref={ref}
      className={cn("relative flex w-full flex-col gap-2", classes?.wrapper)}
    >
      <LabelInput
        htmlFor={id}
        required={props.required}
        className={cn(classes?.label, error && "text-red-500")}
      >
        {label}
      </LabelInput>

      <input
        id={id}
        name={name}
        type="hidden"
        value={typeof value === "string" ? value : ""}
        {...otherProps}
      />

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-between gap-3 rounded-full border px-6 py-[11px]",
          error && "border-red-500",
          classes?.input,
        )}
      >
        <span className="text-base font-medium text-black sm:text-lg">
          {displayValue}
        </span>
        <span className="bg-primary-50 text-primary-default flex h-10 w-10 items-center justify-center rounded-full border border-black/10">
          <Clock3Icon className="h-4 w-4" />
        </span>
      </button>

      {isOpen && (
        <div className="animate-fade-in absolute top-[calc(100%+0.75rem)] left-0 z-50 w-full max-w-[min(560px,calc(100vw-3rem))] overflow-hidden rounded-2xl border border-black/10 bg-white p-3 shadow-[0_30px_70px_rgba(0,0,0,0.18)] sm:p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <div className="col-span-1 flex flex-col gap-2 md:col-span-2 lg:col-span-1 xl:col-span-2">
              <span className="px-1 text-sm text-neutral-500">Period</span>
              <div className="flex overflow-hidden rounded-xl border border-black/15">
                {(["AM", "PM"] as const).map((period) => (
                  <button
                    key={period}
                    type="button"
                    onClick={() => togglePeriod(period)}
                    disabled={props.disabled || props.readOnly}
                    className={cn(
                      "flex h-12 w-full items-center justify-center text-base font-semibold transition-colors",
                      time.period === period
                        ? "bg-primary-100 text-primary-800"
                        : "bg-white text-neutral-600 hover:bg-neutral-50",
                      (props.disabled || props.readOnly) &&
                        "cursor-not-allowed opacity-60",
                    )}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>

            <TimeNumberField
              id={`${id}-hour`}
              label="Hours"
              value={time.hour}
              min={1}
              max={12}
              onChange={updateHour}
              onIncrement={incrementHour}
              onDecrement={decrementHour}
              disabled={props.disabled}
              readOnly={props.readOnly}
              incrementDisabled={incrementDisabled}
              decrementDisabled={decrementDisabled}
            />

            <TimeNumberField
              id={`${id}-minute`}
              label="Minutes"
              value={time.minute}
              min={0}
              max={60}
              onChange={updateMinute}
              onIncrement={incrementMinute}
              onDecrement={decrementMinute}
              disabled={props.disabled}
              readOnly={props.readOnly}
              incrementDisabled={incrementDisabled}
              decrementDisabled={decrementDisabled}
            />
          </div>
        </div>
      )}

      <ErrorInput className={classes?.error}>{error}</ErrorInput>
    </div>
  );
}

type TimeNumberFieldProps = {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  readOnly?: boolean;
  incrementDisabled?: boolean;
  decrementDisabled?: boolean;
  onChange: (value: number) => void;
  onIncrement: () => void;
  onDecrement: () => void;
};

function TimeNumberField({
  id,
  label,
  value,
  min,
  max,
  disabled,
  readOnly,
  incrementDisabled,
  decrementDisabled,
  onChange,
  onIncrement,
  onDecrement,
}: TimeNumberFieldProps) {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(event.target.value);

    if (Number.isNaN(nextValue)) return;

    onChange(nextValue);
  };

  const isDisabled = disabled || readOnly;

  return (
    <div className="flex flex-col gap-2">
      <span className="px-1 text-sm text-neutral-500">{label}</span>
      <div className="flex items-center gap-2 rounded-2xl border border-black/10 bg-neutral-50 p-2">
        <button
          type="button"
          onClick={onDecrement}
          disabled={isDisabled || decrementDisabled}
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-black/10 bg-white transition-colors hover:bg-neutral-100",
            (isDisabled || decrementDisabled) &&
              "cursor-not-allowed opacity-60",
          )}
        >
          <Minus className="h-4 w-4" />
        </button>

        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          readOnly={readOnly}
          className="w-full bg-transparent text-center text-lg font-semibold text-black outline-none sm:text-xl"
        />

        <button
          type="button"
          onClick={onIncrement}
          disabled={isDisabled || incrementDisabled}
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-black/10 bg-white transition-colors hover:bg-neutral-100",
            (isDisabled || incrementDisabled) &&
              "cursor-not-allowed opacity-60",
          )}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
